/**
 * ResilienceScoreCard -- the MARSAD Resilience Score headline (P335).
 *
 * Score 0-100 · Tier A-E · Confidence %, per docs/resilience-score-design.md
 * (all nine decisions locked 2026-07-22). Confidence is shown BESIDE the
 * score and caps the tier (design D3) -- when the cap bit, the card says so:
 * evidence quality bounds how good a score is allowed to look. Rendered at
 * the top of ReadinessView; every figure decomposes into the readiness /
 * crosswalk surfaces beneath it (design rule: never ship the number without
 * the evidence).
 *
 * Hardcoded English to match the sibling shared/ exec views.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { canDownloadReadinessPack } from "shared/api/authority";
import { canReadReadiness } from "shared/api/authority";
import { useMe } from "shared/api/useMe";
import type { ResilienceScore, ScoreSubScore } from "shared/api/types";
import { EngineBusy } from "shared/components/EngineBusy";
import { useToast } from "shared/toast/ToastProvider";
import { downloadScorePack, useResilienceScore } from "shared/readiness/useReadiness";

const TIER_TONE: Record<string, string> = {
  A: "bg-emerald-100 text-emerald-800 ring-emerald-300",
  B: "bg-sky-100 text-sky-800 ring-sky-300",
  C: "bg-amber-100 text-amber-800 ring-amber-300",
  D: "bg-orange-100 text-orange-900 ring-orange-300",
  E: "bg-rose-100 text-rose-800 ring-rose-300",
};

/* Round 11 F3 -- the three pillar names moved to the catalogue. They
   were a hard-coded English map, which `markUntranslated` never sees,
   on the card carrying the one number an executive is asked about. */

function SubScoreRow({ sub }: { sub: ScoreSubScore }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div data-testid={`score-sub-${sub.key}`} className="flex items-center gap-2">
      <span className="w-36 shrink-0 text-xs text-slate-500">
        {t(`screens.resilienceScoreCard.pillar.${sub.key}`, {
          defaultValue: sub.key,
        })}
        {/* ... and a space before the weight. `ms-1` put one on screen
            and none in the text, so the row read "Posture(35)". */}
        {/* ROUND 13 H -- and then say what the number in brackets IS.
            The row read "Exposure (25) 100": a weight in the composite
            beside a pillar's own 0-100 score, two scales on one line
            with nothing separating them, so the pillar read as four
            times its own ceiling. Neither number was wrong. */}
        <span className="ms-1 text-[10px] text-slate-400">
          {" "}
          {t("screens.resilienceScoreCard.pillarWeight", { weight: sub.weight })}
        </span>
      </span>
      <div className="h-1.5 flex-1 rounded bg-paper-2">
        {sub.value !== null && (
          <div
            className="h-1.5 rounded bg-brand-brass"
            style={{ width: `${Math.max(2, Math.min(100, sub.value))}%` }}
          />
        )}
      </div>
      {/* H -- the ceiling, made visible. A bar with no scale beside a
          bracketed weight is where "100" stopped reading as "out of
          100". */}
      <span
        data-testid={`score-sub-value-${sub.key}`}
        className="w-16 shrink-0 text-end text-xs tabular-nums text-slate-700"
      >
        {sub.value === null
          ? "--"
          : t("screens.resilienceScoreCard.pillarOutOf", {
              value: sub.value.toFixed(0),
            })}
      </span>
    </div>
  );
}

/** Round 20 Y3 -- each pillar's inputs, so the figure can be re-performed
 *  from the card. A component with no value on this estate says so. */
function SubScoreInputs({ sub }: { sub: ScoreSubScore }): JSX.Element {
  const { t } = useTranslation();
  return (
    <ul
      data-testid={`score-inputs-${sub.key}`}
      className="ms-36 list-disc space-y-0.5 ps-4 text-[11px] text-slate-500"
    >
      {sub.components.map((c) => (
        <li key={c.key}>
          {c.detail}
          {" -- "}
          {c.value === null
            ? t("screens.resilienceScoreCard.inputNotMeasured")
            : t("screens.resilienceScoreCard.inputValue", {
                value: (c.value * 100).toFixed(0),
              })}
        </li>
      ))}
      <li className="list-none text-slate-400">
        {t(`screens.resilienceScoreCard.pillarRule.${sub.key}`, {
          defaultValue: "",
        })}
      </li>
    </ul>
  );
}

/** Round 20 Y3 -- the composite, written out with the figures in it. */
function scoreFormula(data: ResilienceScore, pillar: (key: string) => string): string {
  const subs = [data.posture, data.exposure, data.outcome].filter(
    (s) => s.value !== null,
  );
  const den = subs.reduce((a, s) => a + s.weight, 0);
  const terms = subs
    .map((s) => `${s.weight} × ${pillar(s.key)} ${s.value!.toFixed(1)}`)
    .join(" + ");
  return `(${terms}) ÷ ${den}`;
}

function Body({ data }: { data: ResilienceScore }): JSX.Element {
  const { t } = useTranslation();
  /* Round 17 B10 -- with nothing admitted there is no score: it read
     "6.0, tier E" on the empty estate and "63.5, tier C (would be B)"
     with "Exposure 100 / 100" over two candidates nobody had assessed.
     No tier, no cap notice, each pillar "--" and its population said. */
  if (data.not_scored_reason) {
    return (
      <>
        <p
          data-testid="score-not-scored"
          data-reason={data.not_scored_reason}
          className="font-display text-2xl text-slate-700"
        >
          {t("screens.resilienceScoreCard.notScored")}
        </p>
        <div className="mt-3 space-y-1.5">
          <SubScoreRow sub={data.posture} />
          <SubScoreRow sub={data.exposure} />
          <SubScoreRow sub={data.outcome} />
        </div>
        <p
          data-testid="score-not-scored-population"
          className="mt-2 text-xs text-slate-500"
        >
          {t("screens.resilienceScoreCard.notScoredPopulation")}
        </p>
      </>
    );
  }
  const liveUnsealed =
    data.score !== null &&
    (data.last_sealed_score === null ||
      data.last_sealed_score !== data.score.toFixed(1));
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span
          data-testid="score-value"
          className="font-display text-5xl text-slate-900 tabular-nums"
        >
          {data.score === null ? "--" : data.score.toFixed(1)}
        </span>
        {/* Round 17 B15 -- the live figure says it is live until the
            next seal; after a seal the card reads one figure. */}
        {liveUnsealed && (
          <span
            data-testid="score-live-unsealed"
            className="rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-800"
          >
            {t("screens.resilienceScoreCard.liveUnsealed")}
          </span>
        )}
        <span
          data-testid="score-tier"
          data-tier={data.tier}
          className={`rounded px-3 py-1 text-lg font-semibold ring-1 ${
            TIER_TONE[data.tier] ?? TIER_TONE.E
          }`}
        >
          {t("screens.resilienceScoreCard.tier")} {data.tier}
        </span>
        <span
          data-testid="score-confidence"
          className="text-sm text-slate-600 tabular-nums"
        >
          {t("screens.resilienceScoreCard.confidence")}{" "}
          <span className="font-semibold">{data.confidence.toFixed(0)}%</span>
        </span>
        <span className="text-[11px] uppercase tracking-wide text-slate-400">
          {/* D4 -- a version number glued onto a translated fragment.
              Reordered, the two swapped and it read "0.5.0 methodology
              v". Interpolated values are bidi-isolated; a stitched one
              is not. */}
          {/* Round 20 Y3 -- the version is a link to the method itself. */}
          <Link
            to="/readiness/score-methodology"
            data-testid="score-methodology-link"
            className="text-brand-brassDark hover:underline"
          >
            {t("screens.resilienceScoreCard.methodologyVersion", {
              version: data.methodology_version,
            })}
          </Link>
        </span>
      </div>

      {data.confidence_capped && (
        <p
          data-testid="score-cap-notice"
          className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800"
        >
          {t("screens.resilienceScoreCard.tierCappedAt")} {data.tier}{" "}
          {t("screens.resilienceScoreCard.wouldBe")} {data.base_tier}
          {t("screens.resilienceScoreCard.confidenceIsBelow50InstrumentMoreOf")}
        </p>
      )}

      <div className="mt-3 space-y-1.5">
        <SubScoreRow sub={data.posture} />
        <SubScoreInputs sub={data.posture} />
        <SubScoreRow sub={data.exposure} />
        <SubScoreInputs sub={data.exposure} />
        <SubScoreRow sub={data.outcome} />
        <SubScoreInputs sub={data.outcome} />
      </div>
      {data.score !== null && (
        <p data-testid="score-formula" className="mt-2 text-xs text-slate-600">
          {t("screens.resilienceScoreCard.formula", {
            score: data.score.toFixed(1),
            expression: scoreFormula(data, (key) =>
              t(`screens.resilienceScoreCard.pillar.${key}`, { defaultValue: key }),
            ),
          })}
        </p>
      )}
      {data.posture_signatures &&
        (!data.posture_signatures.cro || !data.posture_signatures.ops_res) && (
          <p
            data-testid="score-posture-unsigned"
            className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800"
          >
            {t("screens.resilienceScoreCard.postureUnsigned", {
              cro:
                data.posture_signatures.cro ??
                t("screens.resilienceScoreCard.awaiting"),
              opsRes:
                data.posture_signatures.ops_res ??
                t("screens.resilienceScoreCard.awaiting"),
            })}
          </p>
        )}

      {data.maturity_drift !== null && (
        <p className="mt-2 text-xs text-slate-500">
          {t("screens.resilienceScoreCard.boardAssessedMaturityVsLiveEvidenceDrift")}{" "}
          <span className="tabular-nums">
            {data.maturity_drift > 0 ? "+" : ""}
            {data.maturity_drift.toFixed(2)}
          </span>
        </p>
      )}
      <p className="mt-1 text-[11px] text-slate-400">
        {!data.last_sealed_at
          ? t("screens.resilienceScoreCard.notYetSealed")
          : liveUnsealed
            ? t("screens.resilienceScoreCard.lastSealed", {
                score: data.last_sealed_score,
              })
            : t("screens.resilienceScoreCard.sealedSame")}
        {t("screens.resilienceScoreCard.decomposes")}
      </p>
    </>
  );
}

export function ResilienceScoreCard(): JSX.Element {
  const me = useMe();
  const { t } = useTranslation();
  // Round 9 E9 -- it lives inside ReadinessView, which now declines to
  // ask on the reader's behalf. Gated here too so the next surface that
  // mounts it inherits the refusal rather than rediscovering it.
  const { data, isLoading, isError } = useResilienceScore(
    me.isSuccess && canReadReadiness(me.data),
  );
  const toast = useToast();
  /* Round 13 C8 -- the pack takes 4.1 seconds to compose, hash and
     sign, and for all four of them the button said what it said before
     the click. Nothing was wrong; it merely looked as though nothing
     had happened, which is the state that earns a second click. The
     button is disabled while it runs for that reason, not for safety:
     a second request would be answered honestly and would still be
     waste. */
  const [buildingPack, setBuildingPack] = useState(false);

  return (
    <section
      data-testid="resilience-score-card"
      className="rounded-xl border border-paper-2 bg-white p-4 shadow-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          {t("screens.resilienceScoreCard.marsadResilienceScore")}{" "}
          <span className="font-arabic normal-case">درجة المرصد</span>
        </div>
        {/* C2 -- fifth round for this class. The card offered her a
            signed pack and answered "HTTP 403" one paragraph under a
            sentence saying the pack is the approver's -- with the
            header beside it correctly gated. Two buttons for the same
            artefact, one honest.

            Same gate, same words, one helper. */}
        {canDownloadReadinessPack(me.data) ? (
          <button
            type="button"
            data-testid="score-download-pack"
            disabled={buildingPack}
            aria-busy={buildingPack}
            onClick={() => {
              setBuildingPack(true);
              void downloadScorePack()
                .catch((e) =>
                  toast.push({ tone: "error", message: String(e?.message ?? e) }),
                )
                .finally(() => setBuildingPack(false));
            }}
            className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1 text-xs font-medium text-brand-brassDark hover:bg-brand-brass/10 disabled:cursor-wait disabled:opacity-70"
          >
            {buildingPack ? (
              <EngineBusy
                label={t("screens.resilienceScoreCard.buildingPack")}
                testid="score-pack-busy"
              />
            ) : (
              t("screens.resilienceScoreCard.downloadSignedPack")
            )}
          </button>
        ) : (
          <p
            data-testid="score-pack-not-yours"
            className="max-w-xs text-xs text-slate-500"
          >
            {t("screens.resilienceScoreCard.packIsApprovers")}
          </p>
        )}
      </div>
      {isLoading ? (
        <div data-testid="score-loading" className="mt-2 text-sm text-slate-500">
          {t("screens.resilienceScoreCard.loading")}
        </div>
      ) : isError || !data ? (
        <div data-testid="score-error" className="mt-2 text-sm text-slate-500">
          {t("screens.resilienceScoreCard.scoreUnavailable")}
        </div>
      ) : (
        <Body data={data} />
      )}
    </section>
  );
}
